import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell
} from "recharts";
import WorkoutPlan from "./WorkoutPlan";

// ════════════════════════════════════════════════════════════
// 🔧 CONFIGURATION — Update these after setup
// ════════════════════════════════════════════════════════════
const CONFIG = {
  STRAVA_CLIENT_ID: "207161",                                    // From Strava API Settings
  WHOOP_CLIENT_ID: "e00b5eff-3206-4e4f-90ba-794b8d57b474",                       // From WHOOP Developer Portal
  BACKEND_URL: "https://strava-fitness-api.miha-fit-hub.workers.dev",  // Your deployed worker URL
  REDIRECT_URI: "https://fitness-dashboard-0jo.pages.dev",       // Where both Strava & WHOOP redirect back
};

// ════════════════════════════════════════════════════════════
// 🎨 DESIGN SYSTEM — Sports-luxe dark theme
// ════════════════════════════════════════════════════════════
const DARK_NEUTRALS = {
  bg: "#07080c",
  surface: "rgba(255,255,255,0.025)",
  surfaceHover: "rgba(255,255,255,0.05)",
  border: "rgba(255,255,255,0.06)",
  borderActive: "rgba(255,255,255,0.12)",
  text: "#f0f0f2",
  dim: "rgba(255,255,255,0.45)",
  faint: "rgba(255,255,255,0.18)",
};
const LIGHT_NEUTRALS = {
  bg: "#f8fafc",
  surface: "#ffffff",
  surfaceHover: "#f1f5f9",
  border: "#e2e8f0",
  borderActive: "#cbd5e1",
  text: "#0f172a",
  dim: "rgba(15,23,42,0.55)",
  faint: "rgba(15,23,42,0.28)",
};
const C = {
  ...DARK_NEUTRALS,
  // Activity-type accents
  run: "#10b981",       // Emerald — Running
  ride: "#3b82f6",      // Blue — Cycling
  swim: "#06b6d4",      // Cyan — Swimming
  walk: "#a78bfa",      // Purple — Walking
  workout: "#f59e0b",   // Amber — Strength/Workout
  other: "#6b7280",     // Gray — Other
  // Metric accents
  pace: "#10b981",
  distance: "#3b82f6",
  heart: "#ef4444",
  calories: "#f59e0b",
  elevation: "#8b5cf6",
  cadence: "#ec4899",
  // Device colors
  garmin: "#00b4d8",
  whoop: "#22c55e",
  apple: "#f472b6",
  strava: "#fc4c02",
};

const ACTIVITY_TYPES = {
  Run: { icon: "🏃", color: C.run, label: "Running" },
  Ride: { icon: "🚴", color: C.ride, label: "Cycling" },
  Swim: { icon: "🏊", color: C.swim, label: "Swimming" },
  Walk: { icon: "🚶", color: C.walk, label: "Walking" },
  WeightTraining: { icon: "🏋️", color: C.workout, label: "Strength" },
  Workout: { icon: "💪", color: C.workout, label: "Workout" },
  Hike: { icon: "⛰️", color: "#84cc16", label: "Hiking" },
  Yoga: { icon: "🧘", color: "#c084fc", label: "Yoga" },
  VirtualRun: { icon: "🏃", color: C.run, label: "Virtual Run" },
  VirtualRide: { icon: "🚴", color: C.ride, label: "Virtual Ride" },
};

const DEVICE_SIGNATURES = {
  garmin: { patterns: ["garmin", "forerunner", "fenix", "venu", "vivosmart", "instinct", "epix", "enduro"], color: C.garmin, icon: "⌚", label: "Garmin" },
  whoop: { patterns: ["whoop"], color: C.whoop, icon: "🟢", label: "WHOOP" },
  apple: { patterns: ["apple watch", "apple", "iphone"], color: C.apple, icon: "⌚", label: "Apple Watch" },
  strava: { patterns: ["strava"], color: C.strava, icon: "🔶", label: "Strava App" },
};

// ─── Helpers ───
const fmtPace = (secPerKm) => {
  if (!secPerKm || !isFinite(secPerKm)) return "--:--";
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};
const fmtDuration = (secs) => {
  if (!secs) return "0m";
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const fmtKm = (m) => (m / 1000).toFixed(1);
const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const fmtShortDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const detectDevice = (name = "") => {
  const lower = name.toLowerCase();
  for (const [key, dev] of Object.entries(DEVICE_SIGNATURES)) {
    if (dev.patterns.some(p => lower.includes(p))) return { key, ...dev };
  }
  return { key: "unknown", color: C.other, icon: "📱", label: name || "Unknown" };
};

const getActivityType = (type) => ACTIVITY_TYPES[type] || { icon: "🏅", color: C.other, label: type };

// ════════════════════════════════════════════════════════════
// 📊 DEMO DATA — Used when not connected to Strava
// ════════════════════════════════════════════════════════════
const generateDemoData = () => {
  const activities = [];
  const types = ["Run", "Run", "Run", "Run", "Ride", "WeightTraining", "Walk", "Run", "Swim", "Run"];
  const devices = ["Garmin Forerunner 265", "WHOOP 4.0", "Apple Watch Ultra", "Garmin Forerunner 265", "Strava iPhone"];
  const seededRandom = (s) => { let x = Math.sin(s) * 10000; return x - Math.floor(x); };
  let seed = 42;
  const rand = () => { seed++; return seededRandom(seed); };

  const startDate = new Date(2017, 0, 1);
  const endDate = new Date(2025, 1, 28);
  const monthWeight = [0.9, 0.95, 1.0, 0.85, 0.7, 0.5, 0.4, 0.45, 0.65, 1.0, 1.05, 0.95];
  const yearWeight = { 2017: 0.55, 2018: 0.8, 2019: 1.05, 2020: 0.65, 2021: 0.85, 2022: 1.1, 2023: 1.2, 2024: 1.15, 2025: 0.5 };

  let current = new Date(startDate);
  while (current <= endDate && activities.length < 420) {
    const m = current.getMonth();
    const y = current.getFullYear();
    const prob = (monthWeight[m] * (yearWeight[y] || 0.8) * 0.14);

    if (rand() < prob) {
      const type = types[Math.floor(rand() * types.length)];
      const device = devices[Math.floor(rand() * devices.length)];
      const yearProg = Math.min(1, (y - 2017) / 7);

      let distance, movingTime, avgSpeed, avgHR, calories, elevGain, avgCadence;

      if (type === "Run") {
        distance = (3 + rand() * 10) * 1000;
        const paceBase = 390 - yearProg * 80 + rand() * 60 - 30;
        const pace = Math.max(280, Math.min(480, paceBase));
        movingTime = (distance / 1000) * pace;
        avgSpeed = distance / movingTime;
        avgHR = Math.round(142 + rand() * 20 - 5);
        avgCadence = Math.round(165 + rand() * 15);
        elevGain = Math.round(rand() * 80 + 5);
      } else if (type === "Ride") {
        distance = (15 + rand() * 40) * 1000;
        avgSpeed = 20 + rand() * 12;
        movingTime = distance / (avgSpeed * 1000 / 3600);
        avgHR = Math.round(130 + rand() * 25);
        avgCadence = Math.round(80 + rand() * 20);
        elevGain = Math.round(rand() * 300 + 20);
      } else if (type === "Swim") {
        distance = (500 + rand() * 2000);
        avgSpeed = 1.2 + rand() * 0.6;
        movingTime = distance / avgSpeed;
        avgHR = Math.round(135 + rand() * 20);
        avgCadence = 0;
        elevGain = 0;
      } else if (type === "WeightTraining") {
        distance = 0;
        movingTime = (30 + rand() * 50) * 60;
        avgSpeed = 0;
        avgHR = Math.round(115 + rand() * 25);
        avgCadence = 0;
        elevGain = 0;
      } else {
        distance = (2 + rand() * 6) * 1000;
        avgSpeed = 4.5 + rand() * 2;
        movingTime = distance / (avgSpeed * 1000 / 3600);
        avgHR = Math.round(100 + rand() * 20);
        avgCadence = 0;
        elevGain = Math.round(rand() * 40);
      }

      calories = Math.round(movingTime / 60 * (6 + rand() * 4));

      activities.push({
        id: activities.length + 1,
        name: `${type === "Run" ? ["Morning Run", "Evening Run", "Tempo Run", "Long Run", "Easy Run", "Recovery Run"][Math.floor(rand() * 6)] : type === "Ride" ? "Afternoon Ride" : type === "Swim" ? "Pool Swim" : type === "WeightTraining" ? "Gym Session" : "Walk"}`,
        type,
        start_date: new Date(current).toISOString(),
        distance: Math.round(distance),
        moving_time: Math.round(movingTime),
        elapsed_time: Math.round(movingTime * (1 + rand() * 0.15)),
        average_speed: avgSpeed,
        average_heartrate: avgHR,
        max_heartrate: Math.round(avgHR + 15 + rand() * 15),
        total_elevation_gain: elevGain,
        kilojoules: Math.round(calories * 4.184),
        calories,
        average_cadence: avgCadence,
        device_name: device,
        has_heartrate: true,
        suffer_score: Math.round(30 + rand() * 120),
      });
    }
    current.setDate(current.getDate() + 1);
  }
  return activities;
};

// ════════════════════════════════════════════════════════════
// 🧮 ANALYTICS ENGINE
// ════════════════════════════════════════════════════════════
const computeAnalytics = (activities, typeFilter = "all") => {
  const filtered = typeFilter === "all" ? activities : activities.filter(a => a.type === typeFilter);
  if (!filtered.length) return null;

  const totalDist = filtered.reduce((s, a) => s + (a.distance || 0), 0);
  const totalTime = filtered.reduce((s, a) => s + (a.moving_time || 0), 0);
  const totalCals = filtered.reduce((s, a) => s + (a.calories || 0), 0);
  const totalElev = filtered.reduce((s, a) => s + (a.total_elevation_gain || 0), 0);
  const hrActivities = filtered.filter(a => a.average_heartrate > 0);
  const avgHR = hrActivities.length ? Math.round(hrActivities.reduce((s, a) => s + a.average_heartrate, 0) / hrActivities.length) : 0;
  const maxHR = hrActivities.length ? Math.max(...hrActivities.map(a => a.max_heartrate || 0)) : 0;

  // Running-specific
  const runs = filtered.filter(a => a.type === "Run" || a.type === "VirtualRun");
  const runDist = runs.reduce((s, a) => s + (a.distance || 0), 0);
  const runTime = runs.reduce((s, a) => s + (a.moving_time || 0), 0);
  const avgPacePerKm = runDist > 0 ? (runTime / (runDist / 1000)) : 0;
  const bestPace = runs.length ? Math.min(...runs.filter(a => a.distance > 1000).map(a => a.moving_time / (a.distance / 1000))) : 0;

  // 5K PR estimate (runs between 4.8-5.5km or named "5K")
  const potential5ks = runs.filter(a => {
    const km = a.distance / 1000;
    return (km >= 4.8 && km <= 5.5) || a.name?.toLowerCase().includes("5k");
  });
  const pr5k = potential5ks.length ? Math.min(...potential5ks.map(a => a.moving_time * (5000 / a.distance))) : null;

  // By year
  const yearly = {};
  filtered.forEach(a => {
    const y = new Date(a.start_date).getFullYear();
    if (!yearly[y]) yearly[y] = { km: 0, runs: 0, time: 0, elev: 0, cals: 0, bestPace: Infinity, paces: [] };
    yearly[y].km += (a.distance || 0) / 1000;
    yearly[y].runs++;
    yearly[y].time += a.moving_time || 0;
    yearly[y].elev += a.total_elevation_gain || 0;
    yearly[y].cals += a.calories || 0;
    if (a.type === "Run" && a.distance > 1000) {
      const p = a.moving_time / (a.distance / 1000);
      yearly[y].bestPace = Math.min(yearly[y].bestPace, p);
      yearly[y].paces.push(p);
    }
  });

  // By month (last 24)
  const monthly = {};
  filtered.forEach(a => {
    const d = new Date(a.start_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!monthly[key]) monthly[key] = { km: 0, runs: 0, time: 0 };
    monthly[key].km += (a.distance || 0) / 1000;
    monthly[key].runs++;
    monthly[key].time += a.moving_time || 0;
  });

  // By type
  const typeBreakdown = {};
  activities.forEach(a => {
    if (!typeBreakdown[a.type]) typeBreakdown[a.type] = { count: 0, dist: 0, time: 0 };
    typeBreakdown[a.type].count++;
    typeBreakdown[a.type].dist += a.distance || 0;
    typeBreakdown[a.type].time += a.moving_time || 0;
  });

  // By device
  const deviceBreakdown = {};
  activities.forEach(a => {
    const dev = detectDevice(a.device_name);
    if (!deviceBreakdown[dev.key]) deviceBreakdown[dev.key] = { ...dev, count: 0, dist: 0 };
    deviceBreakdown[dev.key].count++;
    deviceBreakdown[dev.key].dist += a.distance || 0;
  });

  // Heatmap
  const heatmap = {};
  filtered.forEach(a => {
    const d = new Date(a.start_date);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    heatmap[key] = (heatmap[key] || 0) + 1;
  });

  // Weekly volume (last 12 weeks)
  const weeklyVol = [];
  const now = new Date();
  for (let w = 11; w >= 0; w--) {
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - (w + 1) * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const weekActs = filtered.filter(a => {
      const d = new Date(a.start_date);
      return d >= weekStart && d < weekEnd;
    });
    weeklyVol.push({
      week: `W${12 - w}`,
      label: `${weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`,
      km: Math.round(weekActs.reduce((s, a) => s + (a.distance || 0), 0) / 1000 * 10) / 10,
      count: weekActs.length,
      time: weekActs.reduce((s, a) => s + (a.moving_time || 0), 0)
    });
  }

  // Pace progression (rolling average for runs)
  const sortedRuns = [...runs].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const paceProgression = [];
  const window = 12;
  for (let i = window; i < sortedRuns.length; i += 2) {
    const slice = sortedRuns.slice(i - window, i);
    const avgP = slice.reduce((s, a) => s + (a.moving_time / (a.distance / 1000)), 0) / slice.length;
    const best = Math.min(...slice.map(a => a.moving_time / (a.distance / 1000)));
    paceProgression.push({
      date: new Date(sortedRuns[i].start_date).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
      avgPace: avgP,
      bestPace: best
    });
  }

  return {
    count: filtered.length, totalDist, totalTime, totalCals, totalElev,
    avgHR, maxHR, avgPacePerKm, bestPace, pr5k,
    avgDistPerRun: filtered.length ? totalDist / filtered.length : 0,
    yearly, monthly, typeBreakdown, deviceBreakdown, heatmap,
    weeklyVol, paceProgression
  };
};

// ════════════════════════════════════════════════════════════
// 🧱 COMPONENTS
// ════════════════════════════════════════════════════════════

const StatCard = ({ icon, label, value, sub, color = C.pace, delay = 0 }) => (
  <div style={{
    background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`,
    padding: "16px 18px", flex: "1 1 150px", minWidth: 150,
    position: "relative", overflow: "hidden",
    animation: `fadeUp 0.5s ease ${delay}s both`
  }}>
    <div style={{
      position: "absolute", top: -30, right: -30, width: 90, height: 90,
      borderRadius: "50%", background: color, opacity: 0.04
    }} />
    <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
    <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.8, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>{sub}</div>}
  </div>
);

const ChartCard = ({ title, subtitle, children, style = {} }) => (
  <div style={{
    background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`,
    padding: "20px", ...style
  }}>
    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 11, color: C.dim, marginBottom: 16 }}>{subtitle}</div>}
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "rgba(7,8,12,0.96)", border: `1px solid ${C.border}`,
      borderRadius: 10, padding: "10px 14px", fontSize: 12,
      backdropFilter: "blur(16px)", boxShadow: "0 8px 32px rgba(0,0,0,0.4)"
    }}>
      <div style={{ opacity: 0.4, marginBottom: 4, fontSize: 10, fontFamily: "'JetBrains Mono'" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: p.color }} />
          <span style={{ opacity: 0.7 }}>{p.name}:</span> {typeof p.value === "number" ? p.value.toFixed(1) : p.value}
        </div>
      ))}
    </div>
  );
};

const DeviceBadge = ({ device }) => {
  const dev = detectDevice(device);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
      background: `${dev.color}15`, color: dev.color, whiteSpace: "nowrap"
    }}>
      {dev.icon} {dev.label}
    </span>
  );
};

const Heatmap = ({ data }) => {
  const years = [...new Set(Object.keys(data).map(k => parseInt(k.split("-")[0])))].sort();
  const months = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
  const maxVal = Math.max(...Object.values(data), 1);

  return (
    <div>
      <div style={{ display: "flex", gap: 2, marginBottom: 4 }}>
        <div style={{ width: 36 }} />
        {months.map((m, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 9, color: C.faint, fontFamily: "'JetBrains Mono'" }}>{m}</div>
        ))}
      </div>
      {years.map(y => (
        <div key={y} style={{ display: "flex", gap: 2, marginBottom: 2 }}>
          <div style={{ width: 36, fontSize: 10, color: C.dim, display: "flex", alignItems: "center", fontFamily: "'JetBrains Mono'" }}>{y}</div>
          {Array.from({ length: 12 }, (_, m) => {
            const val = data[`${y}-${m}`] || 0;
            const intensity = val / maxVal;
            return (
              <div key={m} title={`${months[m]} ${y}: ${val} activities`} style={{
                flex: 1, aspectRatio: "1.4", borderRadius: 3,
                background: val === 0 ? "rgba(255,255,255,0.02)" : `rgba(16,185,129,${0.12 + intensity * 0.78})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 8, color: intensity > 0.5 ? "#fff" : "transparent", fontWeight: 700
              }}>{val || ""}</div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// 🚀 MAIN APP
// ════════════════════════════════════════════════════════════
export default function FitnessDashboard() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [tab, setTab] = useState("overview");
  const [typeFilter, setTypeFilter] = useState("all");
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  useEffect(() => { localStorage.setItem("theme", theme); }, [theme]);
  // eslint-disable-next-line no-unused-vars
  const C = useMemo(() => {
    const accents = {
      run: "#10b981", ride: "#3b82f6", swim: "#06b6d4", walk: "#a78bfa",
      workout: "#f59e0b", other: "#6b7280",
      pace: "#10b981", distance: "#3b82f6", heart: "#ef4444",
      calories: "#f59e0b", elevation: "#8b5cf6", cadence: "#ec4899",
      garmin: "#00b4d8", whoop: "#22c55e", apple: "#f472b6", strava: "#fc4c02",
    };
    return { ...accents, ...(theme === "light" ? LIGHT_NEUTRALS : DARK_NEUTRALS) };
  }, [theme]);
  const [yearFilter, setYearFilter] = useState("all");
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [demoMode, setDemoMode] = useState(true);
  const [editingActivity, setEditingActivity] = useState(null);

  // ─── WHOOP State ───
  const [whoopConnected, setWhoopConnected] = useState(false);
  const [whoopData, setWhoopData] = useState({ workouts: [], recovery: [], sleep: [], cycles: [] });
  const [whoopSyncing, setWhoopSyncing] = useState(false);

  // ─── Local Overrides System ───
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("activity_overrides") || "{}"); }
    catch { return {}; }
  });

  const saveOverride = (activityId, fields) => {
    const updated = { ...overrides, [activityId]: { ...(overrides[activityId] || {}), ...fields, updated_at: new Date().toISOString() } };
    setOverrides(updated);
    localStorage.setItem("activity_overrides", JSON.stringify(updated));
  };

  const clearOverride = (activityId) => {
    const updated = { ...overrides };
    delete updated[activityId];
    setOverrides(updated);
    localStorage.setItem("activity_overrides", JSON.stringify(updated));
  };

  // Apply overrides to activities
  const mergedActivities = useMemo(() => {
    return activities.map(a => {
      const ov = overrides[a.id];
      if (!ov) return a;
      return {
        ...a,
        distance: ov.distance !== undefined ? ov.distance : a.distance,
        moving_time: ov.moving_time !== undefined ? ov.moving_time : a.moving_time,
        calories: ov.calories !== undefined ? ov.calories : a.calories,
        total_elevation_gain: ov.elevation !== undefined ? ov.elevation : a.total_elevation_gain,
        average_heartrate: ov.avg_hr !== undefined ? ov.avg_hr : a.average_heartrate,
        name: ov.name !== undefined ? ov.name : a.name,
        type: ov.type !== undefined ? ov.type : a.type,
        _note: ov.note || "",
        _hasOverride: true,
        _original: a,
      };
    });
  }, [activities, overrides]);

  // ─── Init: Check for OAuth code, saved token, or load demo ───
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state"); // "strava" or "whoop"

    if (code && state && state.startsWith("whoop_auth")) {
      // WHOOP OAuth callback
      handleWhoopCallback(code);
    } else if (code && state === "strava") {
      // Strava OAuth callback (default)
      handleOAuthCallback(code);
    } else {
      // Check for saved Strava token
      const stored = localStorage.getItem("strava_token");
      if (stored) {
        try {
          const tokenData = JSON.parse(stored);
          if (tokenData.access_token) {
            setConnected(true);
            setDemoMode(false);
            if (tokenData.expires_at && tokenData.expires_at < Date.now() / 1000) {
              refreshAndFetch(tokenData.refresh_token);
            } else {
              fetchActivities(tokenData.access_token);
            }
          }
        } catch (e) { console.error("Token parse error:", e); }
      } else {
        // No saved token — load demo
        const demo = generateDemoData();
        setActivities(demo);
        setLoading(false);
        setDemoMode(true);
        setLastSync(new Date().toISOString());
      }
    }
    // Check for saved WHOOP token
    const whoopStored = localStorage.getItem("whoop_token");
    if (whoopStored) {
      try {
        const whoopToken = JSON.parse(whoopStored);
        if (whoopToken.access_token) {
          setWhoopConnected(true);
          if (whoopToken.expires_at && whoopToken.expires_at < Date.now() / 1000) {
            refreshWhoopToken(whoopToken.refresh_token);
          } else {
            fetchWhoopData(whoopToken.access_token);
          }
        }
      } catch (e) { console.error("WHOOP token parse error:", e); }
    }
  }, []);

  const refreshAndFetch = async (refreshToken) => {
    try {
      const res = await fetch(`${CONFIG.BACKEND_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      const newToken = await res.json();
      if (newToken.access_token) {
        localStorage.setItem("strava_token", JSON.stringify(newToken));
        await fetchActivities(newToken.access_token);
      }
    } catch (err) {
      console.error("Refresh error:", err);
      setLoading(false);
    }
  };

  const handleOAuthCallback = async (code) => {
    try {
      const res = await fetch(`${CONFIG.BACKEND_URL}/auth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, redirect_uri: CONFIG.REDIRECT_URI })
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem("strava_token", JSON.stringify(data));
        setConnected(true);
        await fetchActivities(data.access_token);
      }
    } catch (err) {
      console.error("OAuth error:", err);
      // Fall back to demo
      setActivities(generateDemoData());
      setDemoMode(true);
    }
    setLoading(false);
    window.history.replaceState({}, "", window.location.pathname);
  };

  const fetchActivities = async (token) => {
    setSyncing(true);
    try {
      let allActivities = [];
      let page = 1;
      let hasMore = true;

      // Phase 1: Fetch all activity summaries (paginated)
      while (hasMore) {
        const res = await fetch(
          `https://www.strava.com/api/v3/athlete/activities?per_page=200&page=${page}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data.length > 0) {
          allActivities = [...allActivities, ...data];
          page++;
        } else {
          hasMore = false;
        }
      }

      // Phase 2: Fetch detailed data (calories, etc.) for most recent 50 activities
      // Strava's list endpoint doesn't include calories — the detail endpoint does
      // Rate limit: 100 req/15min, 1000/day — 50 detail calls is safe
      const DETAIL_COUNT = 50;
      const recentIds = allActivities.slice(0, DETAIL_COUNT);
      const detailMap = {};

      // Fetch in batches of 5 to avoid hammering the API
      for (let i = 0; i < recentIds.length; i += 5) {
        const batch = recentIds.slice(i, i + 5);
        const details = await Promise.all(
          batch.map(a =>
            fetch(`https://www.strava.com/api/v3/activities/${a.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.json()).catch(() => null)
          )
        );
        details.forEach(d => {
          if (d && d.id) {
            detailMap[d.id] = {
              calories: d.calories || 0,
              description: d.description || "",
              perceived_exertion: d.perceived_exertion || null,
              suffer_score: d.suffer_score || null,  // Strava's "Relative Effort"
              gear: d.gear || null,
            };
          }
        });
        // Small delay between batches to be respectful of rate limits
        if (i + 5 < recentIds.length) await new Promise(r => setTimeout(r, 200));
      }

      // Merge detailed data back into activities
      const enriched = allActivities.map(a => {
        const detail = detailMap[a.id];
        if (detail) {
          return { ...a, ...detail, _hasDetail: true };
        }
        return a;
      });

      setActivities(enriched);
      setLastSync(new Date().toISOString());
      setDemoMode(false);
    } catch (err) {
      console.error("Fetch error:", err);
    }
    setSyncing(false);
  };

  const connectStrava = () => {
    const scope = "activity:read_all,profile:read_all";
    const url = `https://www.strava.com/oauth/authorize?client_id=${CONFIG.STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(CONFIG.REDIRECT_URI)}&response_type=code&scope=${scope}&state=strava`;
    window.location.href = url;
  };

  // ─── WHOOP OAuth & Data ───
  const connectWhoop = () => {
    const scope = "offline read:recovery read:cycles read:workout read:sleep read:profile read:body_measurement";
    const url = `https://api.prod.whoop.com/oauth/oauth2/auth?client_id=${CONFIG.WHOOP_CLIENT_ID}&redirect_uri=${encodeURIComponent(CONFIG.REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(scope)}&state=whoop_auth_${Date.now()}`;
    window.location.href = url;
  };

  const handleWhoopCallback = async (code) => {
    try {
      const res = await fetch(`${CONFIG.BACKEND_URL}/whoop/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, redirect_uri: CONFIG.REDIRECT_URI })
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem("whoop_token", JSON.stringify(data));
        setWhoopConnected(true);
        await fetchWhoopData(data.access_token);
      } else {
        console.error("WHOOP token error:", data);
      }
    } catch (err) {
      console.error("WHOOP OAuth error:", err);
    }
    // Also load Strava data if we have a token
    const stravaStored = localStorage.getItem("strava_token");
    if (stravaStored) {
      try {
        const t = JSON.parse(stravaStored);
        if (t.access_token) { setConnected(true); setDemoMode(false); await fetchActivities(t.access_token); }
      } catch (e) {}
    } else {
      setActivities(generateDemoData()); setDemoMode(true);
    }
    setLoading(false);
    window.history.replaceState({}, "", window.location.pathname);
  };

  const refreshWhoopToken = async (refreshToken) => {
    try {
      const res = await fetch(`${CONFIG.BACKEND_URL}/whoop/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      const newToken = await res.json();
      if (newToken.access_token) {
        localStorage.setItem("whoop_token", JSON.stringify(newToken));
        await fetchWhoopData(newToken.access_token);
      }
    } catch (err) { console.error("WHOOP refresh error:", err); }
  };

  const fetchWhoopData = async (token) => {
    setWhoopSyncing(true);
    const headers = { Authorization: `Bearer ${token}` };
    const BASE = "https://api.prod.whoop.com/developer";

    try {
      // Fetch all data types in parallel — last 90 days
      const since = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();

      const fetchPaginated = async (endpoint, params = "") => {
        let allRecords = [];
        let nextToken = null;
        let pages = 0;
        do {
          const tokenParam = nextToken ? `&nextToken=${nextToken}` : "";
          const res = await fetch(`${BASE}${endpoint}?limit=25&start=${since}${params}${tokenParam}`, { headers });
          if (!res.ok) { console.error(`WHOOP ${endpoint} error:`, res.status); break; }
          const data = await res.json();
          if (data.records) allRecords = [...allRecords, ...data.records];
          nextToken = data.next_token || null;
          pages++;
          if (pages > 20) break; // Safety limit
        } while (nextToken);
        return allRecords;
      };

      const [workouts, recovery, sleep, cycles] = await Promise.all([
        fetchPaginated("/v2/activity/workout"),
        fetchPaginated("/v2/recovery"),
        fetchPaginated("/v2/activity/sleep"),
        fetchPaginated("/v2/cycle"),
      ]);

      setWhoopData({ workouts, recovery, sleep, cycles });
      console.log(`WHOOP sync: ${workouts.length} workouts, ${recovery.length} recoveries, ${sleep.length} sleeps, ${cycles.length} cycles`);
    } catch (err) {
      console.error("WHOOP fetch error:", err);
    }
    setWhoopSyncing(false);
  };

  const handleWhoopSync = async () => {
    const stored = localStorage.getItem("whoop_token");
    if (stored) {
      const tokenData = JSON.parse(stored);
      if (tokenData.expires_at && tokenData.expires_at < Date.now() / 1000) {
        await refreshWhoopToken(tokenData.refresh_token);
      } else {
        await fetchWhoopData(tokenData.access_token);
      }
    }
  };

  const handleSync = async () => {
    const stored = localStorage.getItem("strava_token");
    if (stored) {
      const tokenData = JSON.parse(stored);
      // Check if token expired
      if (tokenData.expires_at && tokenData.expires_at < Date.now() / 1000) {
        // Refresh token via backend
        try {
          const res = await fetch(`${CONFIG.BACKEND_URL}/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: tokenData.refresh_token })
          });
          const newToken = await res.json();
          localStorage.setItem("strava_token", JSON.stringify(newToken));
          await fetchActivities(newToken.access_token);
        } catch (err) {
          console.error("Refresh error:", err);
        }
      } else {
        await fetchActivities(tokenData.access_token);
      }
    }
  };

  // ─── Filtered data ───
  const filtered = useMemo(() => {
    let result = mergedActivities;
    if (yearFilter !== "all") {
      result = result.filter(a => new Date(a.start_date).getFullYear() === parseInt(yearFilter));
    }
    return result;
  }, [mergedActivities, yearFilter]);

  const analytics = useMemo(() => computeAnalytics(filtered, typeFilter), [filtered, typeFilter]);
  const allAnalytics = useMemo(() => computeAnalytics(mergedActivities, "all"), [mergedActivities]);

  // Available years
  const availableYears = useMemo(() => {
    const years = [...new Set(mergedActivities.map(a => new Date(a.start_date).getFullYear()))].sort((a, b) => b - a);
    return years;
  }, [mergedActivities]);

  // Type filter options
  const availableTypes = useMemo(() => {
    const types = {};
    mergedActivities.forEach(a => { types[a.type] = (types[a.type] || 0) + 1; });
    return Object.entries(types).sort(([, a], [, b]) => b - a);
  }, [mergedActivities]);

  // Yearly chart
  const yearlyChart = useMemo(() => {
    if (!allAnalytics?.yearly) return [];
    return Object.entries(allAnalytics.yearly)
      .sort(([a], [b]) => a - b)
      .map(([year, d]) => ({
        year, km: Math.round(d.km), runs: d.runs,
        avgPace: d.paces?.length ? d.paces.reduce((s, p) => s + p, 0) / d.paces.length : 0,
        bestPace: d.bestPace === Infinity ? 0 : d.bestPace
      }));
  }, [allAnalytics]);

  // Override count
  const overrideCount = Object.keys(overrides).length;

  // Recent activities
  const recentActivities = useMemo(() => {
    const f = typeFilter === "all" ? filtered : filtered.filter(a => a.type === typeFilter);
    return [...f].sort((a, b) => new Date(b.start_date) - new Date(a.start_date)).slice(0, 30);
  }, [filtered, typeFilter]);

  // Type pie
  const typePie = useMemo(() => {
    if (!allAnalytics?.typeBreakdown) return [];
    return Object.entries(allAnalytics.typeBreakdown)
      .sort(([, a], [, b]) => b.count - a.count)
      .map(([type, d]) => ({
        name: getActivityType(type).label,
        value: d.count,
        color: getActivityType(type).color,
        dist: d.dist, time: d.time
      }));
  }, [allAnalytics]);

  // Device pie
  const devicePie = useMemo(() => {
    if (!allAnalytics?.deviceBreakdown) return [];
    return Object.values(allAnalytics.deviceBreakdown)
      .sort((a, b) => b.count - a.count)
      .map(d => ({ name: d.label, value: d.count, color: d.color, dist: d.dist }));
  }, [allAnalytics]);

  // ─── WHOOP Computed Data (must be before early returns — Rules of Hooks) ───
  const whoopRecoveryChart = useMemo(() => {
    return whoopData.recovery
      .filter(r => r.score_state === "SCORED" && r.score)
      .map(r => ({
        date: new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        recovery: Math.round(r.score.recovery_score),
        hrv: Math.round(r.score.hrv_rmssd_milli * 10) / 10,
        rhr: Math.round(r.score.resting_heart_rate),
        spo2: r.score.spo2_percentage ? Math.round(r.score.spo2_percentage * 10) / 10 : null,
        skinTemp: r.score.skin_temp_celsius ? Math.round(r.score.skin_temp_celsius * 10) / 10 : null,
      }))
      .reverse();
  }, [whoopData.recovery]);

  const whoopSleepChart = useMemo(() => {
    return whoopData.sleep
      .filter(s => s.score_state === "SCORED" && s.score && !s.nap)
      .map(s => {
        const stages = s.score.stage_summary;
        const toHrs = ms => Math.round(ms / 3600000 * 10) / 10;
        return {
          date: new Date(s.start).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          total: toHrs(stages.total_in_bed_time_milli - stages.total_awake_time_milli),
          deep: toHrs(stages.total_slow_wave_sleep_time_milli),
          rem: toHrs(stages.total_rem_sleep_time_milli),
          light: toHrs(stages.total_light_sleep_time_milli),
          awake: toHrs(stages.total_awake_time_milli),
          performance: s.score.sleep_performance_percentage ? Math.round(s.score.sleep_performance_percentage) : null,
          efficiency: s.score.sleep_efficiency_percentage ? Math.round(s.score.sleep_efficiency_percentage) : null,
          consistency: s.score.sleep_consistency_percentage ? Math.round(s.score.sleep_consistency_percentage) : null,
          respiratory: s.score.respiratory_rate ? Math.round(s.score.respiratory_rate * 10) / 10 : null,
        };
      })
      .reverse();
  }, [whoopData.sleep]);

  const whoopStrainChart = useMemo(() => {
    return whoopData.cycles
      .filter(c => c.score_state === "SCORED" && c.score)
      .map(c => ({
        date: new Date(c.start).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        strain: Math.round(c.score.strain * 10) / 10,
        calories: Math.round(c.score.kilojoule / 4.184),
        avgHr: c.score.average_heart_rate,
        maxHr: c.score.max_heart_rate,
      }))
      .reverse();
  }, [whoopData.cycles]);

  const whoopWorkoutList = useMemo(() => {
    return whoopData.workouts
      .filter(w => w.score_state === "SCORED" && w.score)
      .slice(0, 20)
      .map(w => ({
        id: w.id,
        sport: w.sport_name || "Unknown",
        date: new Date(w.start).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        strain: Math.round(w.score.strain * 10) / 10,
        calories: Math.round(w.score.kilojoule / 4.184),
        avgHr: w.score.average_heart_rate,
        maxHr: w.score.max_heart_rate,
        distance: w.score.distance_meter ? Math.round(w.score.distance_meter / 10) / 100 : null,
        zones: w.score.zone_durations,
      }));
  }, [whoopData.workouts]);

  const whoopSummary = useMemo(() => {
    const recoveries = whoopData.recovery.filter(r => r.score_state === "SCORED" && r.score);
    const avgRecovery = recoveries.length ? Math.round(recoveries.reduce((s, r) => s + r.score.recovery_score, 0) / recoveries.length) : 0;
    const avgHrv = recoveries.length ? Math.round(recoveries.reduce((s, r) => s + r.score.hrv_rmssd_milli, 0) / recoveries.length * 10) / 10 : 0;
    const avgRhr = recoveries.length ? Math.round(recoveries.reduce((s, r) => s + r.score.resting_heart_rate, 0) / recoveries.length) : 0;
    const sleeps = whoopData.sleep.filter(s => s.score_state === "SCORED" && s.score && !s.nap);
    const avgSleepHrs = sleeps.length ? Math.round(sleeps.reduce((s, sl) => {
      const stages = sl.score.stage_summary;
      return s + (stages.total_in_bed_time_milli - stages.total_awake_time_milli) / 3600000;
    }, 0) / sleeps.length * 10) / 10 : 0;
    const cycles = whoopData.cycles.filter(c => c.score_state === "SCORED" && c.score);
    const avgStrain = cycles.length ? Math.round(cycles.reduce((s, c) => s + c.score.strain, 0) / cycles.length * 10) / 10 : 0;
    return { avgRecovery, avgHrv, avgRhr, avgSleepHrs, avgStrain, days: recoveries.length };
  }, [whoopData]);

  const tabs = [
    { id: "overview", label: "Overview", icon: "📊" },
    { id: "trends", label: "Trends", icon: "📈" },
    { id: "devices", label: "Devices", icon: "⌚" },
    { id: "log", label: "Activity Log", icon: "📋" },
    { id: "training", label: "Training", icon: "💪" },
    ...(whoopConnected ? [{ id: "whoop", label: "WHOOP", icon: "💚" }] : []),
  ];

  // Loading screen
  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16, animation: "pulse 1.5s infinite" }}>🏃</div>
          <div style={{ color: C.dim, fontSize: 14 }}>Loading your fitness data...</div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif", color: C.text }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>No activities found</div>
          <div style={{ color: C.dim, marginTop: 8 }}>Try adjusting your filters</div>
        </div>
      </div>
    );
  }

  // ─── Edit Modal ───
  const EditModal = ({ activity, onSave, onClear, onClose }) => {
    const ov = overrides[activity.id] || {};
    const orig = activity._original || activity;
    const [form, setForm] = useState({
      distKm: ov.distance !== undefined ? (ov.distance / 1000).toFixed(2) : orig.distance > 0 ? (orig.distance / 1000).toFixed(2) : "",
      durationMin: ov.moving_time !== undefined ? (ov.moving_time / 60).toFixed(1) : orig.moving_time > 0 ? (orig.moving_time / 60).toFixed(1) : "",
      calories: ov.calories !== undefined ? String(ov.calories) : orig.calories > 0 ? String(orig.calories) : "",
      elevation: ov.elevation !== undefined ? String(ov.elevation) : orig.total_elevation_gain > 0 ? String(Math.round(orig.total_elevation_gain)) : "",
      avgHr: ov.avg_hr !== undefined ? String(ov.avg_hr) : orig.average_heartrate > 0 ? String(Math.round(orig.average_heartrate)) : "",
      name: ov.name !== undefined ? ov.name : orig.name || "",
      note: ov.note || "",
    });
    const hasExistingOverride = !!overrides[activity.id];

    const handleSave = () => {
      const fields = {};
      if (form.distKm !== "" && parseFloat(form.distKm) * 1000 !== orig.distance) fields.distance = parseFloat(form.distKm) * 1000;
      if (form.durationMin !== "" && parseFloat(form.durationMin) * 60 !== orig.moving_time) fields.moving_time = parseFloat(form.durationMin) * 60;
      if (form.calories !== "" && parseInt(form.calories) !== orig.calories) fields.calories = parseInt(form.calories);
      if (form.elevation !== "" && parseInt(form.elevation) !== Math.round(orig.total_elevation_gain || 0)) fields.elevation = parseInt(form.elevation);
      if (form.avgHr !== "" && parseInt(form.avgHr) !== Math.round(orig.average_heartrate || 0)) fields.avg_hr = parseInt(form.avgHr);
      if (form.name !== orig.name) fields.name = form.name;
      if (form.note.trim()) fields.note = form.note.trim();
      if (Object.keys(fields).length > 0) onSave(activity.id, fields);
      onClose();
    };

    const inputStyle = (isChanged) => ({
      width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13,
      border: `1px solid ${isChanged ? C.pace : C.border}`,
      background: isChanged ? "rgba(16,185,129,0.06)" : "rgba(255,255,255,0.04)",
      color: C.text, fontFamily: "'JetBrains Mono'", outline: "none"
    });

    const labelStyle = { fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4, fontFamily: "'JetBrains Mono'" };

    return (
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          background: "#0d0f14", borderRadius: 16, border: `1px solid ${C.borderActive}`,
          padding: 24, width: "100%", maxWidth: 440, maxHeight: "85vh", overflowY: "auto",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)"
        }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>✏️ Edit Activity</div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
                {fmtDate(orig.start_date)} · {getActivityType(orig.type).icon} {getActivityType(orig.type).label}
              </div>
            </div>
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.06)", border: "none", color: C.dim,
              width: 28, height: 28, borderRadius: 8, cursor: "pointer", fontSize: 14
            }}>✕</button>
          </div>

          {/* Strava original values hint */}
          <div style={{
            padding: "8px 12px", borderRadius: 8, marginBottom: 16,
            background: "rgba(252,76,2,0.06)", border: "1px solid rgba(252,76,2,0.15)",
            fontSize: 10, color: C.dim
          }}>
            📌 Strava originals: {orig.distance > 0 ? `${(orig.distance / 1000).toFixed(2)} km` : "no distance"} · {fmtDuration(orig.moving_time)} · {orig.calories || 0} cal · {Math.round(orig.average_heartrate || 0)} bpm
          </div>

          {/* Form fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={labelStyle}>Distance (km)</div>
              <input type="number" step="0.01" value={form.distKm} onChange={e => setForm(f => ({ ...f, distKm: e.target.value }))}
                style={inputStyle(form.distKm !== "" && parseFloat(form.distKm) * 1000 !== orig.distance)} placeholder="0.00" />
            </div>
            <div>
              <div style={labelStyle}>Duration (minutes)</div>
              <input type="number" step="0.1" value={form.durationMin} onChange={e => setForm(f => ({ ...f, durationMin: e.target.value }))}
                style={inputStyle(form.durationMin !== "" && parseFloat(form.durationMin) * 60 !== orig.moving_time)} placeholder="0.0" />
            </div>
            <div>
              <div style={labelStyle}>Calories</div>
              <input type="number" value={form.calories} onChange={e => setForm(f => ({ ...f, calories: e.target.value }))}
                style={inputStyle(form.calories !== "" && parseInt(form.calories) !== orig.calories)} placeholder="0" />
            </div>
            <div>
              <div style={labelStyle}>Elevation (m)</div>
              <input type="number" value={form.elevation} onChange={e => setForm(f => ({ ...f, elevation: e.target.value }))}
                style={inputStyle(form.elevation !== "" && parseInt(form.elevation) !== Math.round(orig.total_elevation_gain || 0))} placeholder="0" />
            </div>
            <div>
              <div style={labelStyle}>Avg Heart Rate (bpm)</div>
              <input type="number" value={form.avgHr} onChange={e => setForm(f => ({ ...f, avgHr: e.target.value }))}
                style={inputStyle(form.avgHr !== "" && parseInt(form.avgHr) !== Math.round(orig.average_heartrate || 0))} placeholder="0" />
            </div>
            <div>
              <div style={labelStyle}>Activity Name</div>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                style={inputStyle(form.name !== orig.name)} placeholder="Activity name" />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <div style={labelStyle}>Notes / Annotation</div>
            <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              rows={2} placeholder="Add a personal note about this activity..."
              style={{
                ...inputStyle(form.note.trim() !== ""),
                resize: "vertical", fontFamily: "'Outfit'", minHeight: 50
              }} />
          </div>

          {/* Info */}
          <div style={{ fontSize: 10, color: C.faint, marginBottom: 16, lineHeight: 1.5 }}>
            💡 Changed fields glow green. Overrides are stored locally in your browser — Strava data stays untouched.
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8 }}>
            {hasExistingOverride && (
              <button onClick={() => { onClear(activity.id); onClose(); }} style={{
                padding: "9px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                border: `1px solid rgba(239,68,68,0.3)`, background: "rgba(239,68,68,0.08)",
                color: "#ef4444", cursor: "pointer", fontFamily: "'Outfit'"
              }}>Reset to Strava</button>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={onClose} style={{
              padding: "9px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600,
              border: `1px solid ${C.border}`, background: "transparent",
              color: C.dim, cursor: "pointer", fontFamily: "'Outfit'"
            }}>Cancel</button>
            <button onClick={handleSave} style={{
              padding: "9px 20px", borderRadius: 8, fontSize: 12, fontWeight: 700,
              border: "none", background: C.pace, color: "#fff",
              cursor: "pointer", fontFamily: "'Outfit'",
              boxShadow: "0 4px 16px rgba(16,185,129,0.3)"
            }}>Save Override</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      fontFamily: "'Outfit', system-ui, sans-serif",
      background: `linear-gradient(170deg, ${C.bg} 0%, #0b0d14 50%, #080c0a 100%)`,
      color: C.text, minHeight: "100vh"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 4px; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Ambient glow */}
      <div style={{ position: "fixed", top: -300, right: -200, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.04) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "fixed", bottom: -200, left: -200, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.03) 0%, transparent 70%)", pointerEvents: "none" }} />

      {/* ═══ HEADER ═══ */}
      <header style={{
        padding: "16px 20px 0", position: "sticky", top: 0, zIndex: 100,
        background: `linear-gradient(180deg, ${C.bg}ee 70%, ${C.bg}00)`,
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)"
      }}>
        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 11,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, boxShadow: "0 4px 24px rgba(16,185,129,0.25)"
            }}>🏃</div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}>Fitness Command Center</h1>
              <div style={{ fontSize: 10, color: C.dim, fontFamily: "'JetBrains Mono'", display: "flex", alignItems: "center", gap: 6 }}>
                {demoMode ? (
                  <><span style={{ width: 6, height: 6, borderRadius: 99, background: C.calories, display: "inline-block" }} />DEMO MODE</>
                ) : (
                  <><span style={{ width: 6, height: 6, borderRadius: 99, background: C.pace, display: "inline-block", animation: "pulse 2s infinite" }} />
                  LIVE · Strava Connected</>
                )}
                {" · "}{analytics.count} activities · {fmtKm(analytics.totalDist)} km
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Sync button */}
            {!demoMode && (
              <button onClick={() => { handleSync(); if (whoopConnected) handleWhoopSync(); }} disabled={syncing || whoopSyncing} style={{
                padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                border: `1px solid ${C.border}`, background: C.surface,
                color: (syncing || whoopSyncing) ? C.dim : C.text, cursor: (syncing || whoopSyncing) ? "default" : "pointer",
                fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 6
              }}>
                <span style={(syncing || whoopSyncing) ? { animation: "spin 1s linear infinite", display: "inline-block" } : {}}>🔄</span>
                {(syncing || whoopSyncing) ? "Syncing..." : "Sync Now"}
              </button>
            )}
            {/* WHOOP Connect/Status */}
            {!whoopConnected ? (
              <button onClick={connectWhoop} style={{
                padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                border: "none", background: "#44b700", color: "#fff",
                cursor: "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 5,
                boxShadow: "0 4px 16px rgba(68,183,0,0.25)"
              }}>
                💚 Connect WHOOP
              </button>
            ) : (
              <div style={{
                padding: "5px 10px", borderRadius: 6, fontSize: 9, fontWeight: 600,
                background: "rgba(68,183,0,0.1)", color: "#44b700", border: "1px solid rgba(68,183,0,0.2)"
              }}>💚 WHOOP {whoopSyncing ? "syncing..." : `${whoopData.recovery.length}d`}</div>
            )}
            {/* Strava Connect button */}
            {demoMode && (
              <button onClick={connectStrava} style={{
                padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                border: "none", background: "#fc4c02", color: "#fff",
                cursor: "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 6,
                boxShadow: "0 4px 16px rgba(252,76,2,0.3)"
              }}>
                🔗 Connect Strava
              </button>
            )}
          </div>
        </div>

        {/* Filters row */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
          {/* Year filter */}
          <div style={{ display: "flex", gap: 3 }}>
            {["all", ...availableYears].map(y => (
              <button key={y} onClick={() => setYearFilter(y)} style={{
                padding: "4px 9px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                border: `1px solid ${yearFilter === y ? C.pace : C.border}`,
                background: yearFilter === y ? "rgba(16,185,129,0.12)" : "transparent",
                color: yearFilter === y ? C.pace : C.dim,
                cursor: "pointer", fontFamily: "'JetBrains Mono'"
              }}>{y === "all" ? "ALL" : y}</button>
            ))}
          </div>
          <div style={{ width: 1, height: 20, background: C.border, margin: "0 4px" }} />
          {/* Type filter */}
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <button onClick={() => setTypeFilter("all")} style={{
              padding: "4px 9px", borderRadius: 6, fontSize: 10, fontWeight: 600,
              border: `1px solid ${typeFilter === "all" ? C.pace : C.border}`,
              background: typeFilter === "all" ? "rgba(16,185,129,0.12)" : "transparent",
              color: typeFilter === "all" ? C.pace : C.dim,
              cursor: "pointer", fontFamily: "'JetBrains Mono'"
            }}>All Types</button>
            {availableTypes.slice(0, 6).map(([type, count]) => {
              const at = getActivityType(type);
              return (
                <button key={type} onClick={() => setTypeFilter(type)} style={{
                  padding: "4px 9px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                  border: `1px solid ${typeFilter === type ? at.color : C.border}`,
                  background: typeFilter === type ? `${at.color}18` : "transparent",
                  color: typeFilter === type ? at.color : C.dim,
                  cursor: "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 4
                }}>{at.icon} {at.label} <span style={{ opacity: 0.5 }}>{count}</span></button>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${C.border}`, alignItems: "center" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "10px 16px", fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? C.text : C.dim, background: "transparent", border: "none",
              borderBottom: tab === t.id ? `2px solid ${C.pace}` : "2px solid transparent",
              cursor: "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 5
            }}>{t.icon} {t.label}</button>
          ))}
          <button
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            title={theme === "light" ? "Switch to dark" : "Switch to light"}
            style={{
              marginLeft: "auto", padding: "6px 10px", fontSize: 14, border: `1px solid ${C.border}`,
              background: C.surface, color: C.text, borderRadius: 8, cursor: "pointer", fontFamily: "'Outfit'"
            }}
          >{theme === "light" ? "🌙" : "☀️"}</button>
        </div>
      </header>

      {/* ═══ CONTENT ═══ */}
      <main style={{ padding: "20px", maxWidth: 1200, margin: "0 auto" }}>

        {/* ═══ OVERVIEW ═══ */}
        {tab === "overview" && (
          <div>
            {/* Demo banner */}
            {demoMode && (
              <div style={{
                padding: "12px 16px", borderRadius: 10, marginBottom: 16,
                background: "rgba(252,76,2,0.08)", border: "1px solid rgba(252,76,2,0.2)",
                display: "flex", alignItems: "center", gap: 10, fontSize: 13
              }}>
                <span style={{ fontSize: 18 }}>⚡</span>
                <div>
                  <strong>Demo Mode</strong> — Showing simulated data based on your known stats (389+ activities, 1,423+ km, 5K PR 23:50).
                  <span style={{ color: C.strava, fontWeight: 600, cursor: "pointer" }} onClick={connectStrava}> Connect Strava for real data →</span>
                </div>
              </div>
            )}

            {/* KPI Row */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <StatCard icon="📏" label="Total Distance" value={`${fmtKm(analytics.totalDist)} km`} sub={`${analytics.count} activities`} color={C.distance} delay={0} />
              <StatCard icon="⚡" label="5K PR" value={analytics.pr5k ? fmtDuration(analytics.pr5k) : "23:50"} sub={`Pace: ${analytics.pr5k ? fmtPace(analytics.pr5k / 5) : "4:46"}/km`} color={C.pace} delay={0.05} />
              <StatCard icon="⏱️" label="Avg Pace" value={`${fmtPace(analytics.avgPacePerKm)}/km`} sub={`Best: ${fmtPace(analytics.bestPace)}/km`} color={C.calories} delay={0.1} />
              <StatCard icon="💓" label="Avg Heart Rate" value={`${analytics.avgHR} bpm`} sub={`Max: ${analytics.maxHR} bpm`} color={C.heart} delay={0.15} />
              <StatCard icon="🔥" label="Calories Burned" value={analytics.totalCals.toLocaleString()} sub={`${Math.round(analytics.totalCals / analytics.count)} cal/activity`} color={C.calories} delay={0.2} />
              <StatCard icon="⛰️" label="Elevation Gain" value={`${Math.round(analytics.totalElev).toLocaleString()} m`} sub={`${fmtDuration(analytics.totalTime)} total time`} color={C.elevation} delay={0.25} />
            </div>

            {/* Charts row */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              {/* Distance by Year */}
              <ChartCard title="Distance by Year" subtitle="Kilometers per calendar year across all activities" style={{ flex: "2 1 400px" }}>
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={yearlyChart} barSize={26}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                    <XAxis dataKey="year" tick={{ fill: C.dim, fontSize: 10, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: C.faint, fontSize: 10, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="km" name="Distance (km)" fill={C.distance} radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Activity Types */}
              <ChartCard title="Activity Types" subtitle={`Distribution of ${allAnalytics?.count || 0} activities`} style={{ flex: "1 1 250px" }}>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={typePie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={68} strokeWidth={0} paddingAngle={2}>
                      {typePie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: "rgba(7,8,12,0.96)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 12 }}>
                          <div style={{ fontWeight: 700, color: d.color }}>{d.name}</div>
                          <div style={{ color: C.dim }}>{d.value} activities · {fmtKm(d.dist)} km</div>
                        </div>
                      );
                    }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {typePie.map((t, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 2, background: t.color }} />
                      <span style={{ color: C.dim }}>{t.name}</span>
                      <span style={{ fontWeight: 700, fontFamily: "'JetBrains Mono'" }}>{t.value}</span>
                    </div>
                  ))}
                </div>
              </ChartCard>
            </div>

            {/* Pace Evolution */}
            {analytics.paceProgression?.length > 0 && (
              <ChartCard title="Running Pace Evolution" subtitle="Rolling 12-run average (min/km) — lower is faster" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={analytics.paceProgression}>
                    <defs>
                      <linearGradient id="pGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.pace} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={C.pace} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                    <XAxis dataKey="date" tick={{ fill: C.dim, fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis reversed domain={["auto", "auto"]} tick={{ fill: C.faint, fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} tickFormatter={fmtPace} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div style={{ background: "rgba(7,8,12,0.96)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 12 }}>
                          <div style={{ opacity: 0.4, fontSize: 10, marginBottom: 4 }}>{label}</div>
                          <div style={{ color: C.pace, fontWeight: 600 }}>Avg: {fmtPace(payload[0]?.value)}/km</div>
                          {payload[1] && <div style={{ color: C.calories, fontWeight: 600 }}>Best: {fmtPace(payload[1]?.value)}/km</div>}
                        </div>
                      );
                    }} />
                    <Area type="monotone" dataKey="avgPace" stroke={C.pace} fill="url(#pGrad)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="bestPace" stroke={C.calories} strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* Weekly Volume */}
            {analytics.weeklyVol?.length > 0 && (
              <ChartCard title="Weekly Training Load" subtitle="Last 12 weeks — distance and activity count">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={analytics.weeklyVol} barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                    <XAxis dataKey="label" tick={{ fill: C.dim, fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: C.faint, fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="km" name="Distance (km)" fill={C.pace} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        )}

        {/* ═══ TRENDS ═══ */}
        {tab === "trends" && (
          <div>
            {/* YoY Table */}
            <ChartCard title="Year-over-Year Comparison" subtitle="All metrics by calendar year" style={{ marginBottom: 16 }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {["Year", "Activities", "Distance", "Time", "Elevation", "Avg Pace", "Best Pace", "Avg/Run"].map(h => (
                        <th key={h} style={{ padding: "8px 8px", textAlign: "left", fontSize: 9, color: C.dim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "'JetBrains Mono'" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyChart.map((y, i) => {
                      const yd = allAnalytics.yearly[y.year];
                      return (
                        <tr key={y.year} style={{ borderBottom: `1px solid rgba(255,255,255,0.03)`, background: i % 2 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                          <td style={{ padding: "9px 8px", fontWeight: 700, fontFamily: "'JetBrains Mono'" }}>{y.year}</td>
                          <td style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono'" }}>{y.runs}</td>
                          <td style={{ padding: "9px 8px", color: C.distance, fontWeight: 600, fontFamily: "'JetBrains Mono'" }}>{y.km} km</td>
                          <td style={{ padding: "9px 8px", color: C.dim, fontFamily: "'JetBrains Mono'", fontSize: 11 }}>{fmtDuration(yd?.time || 0)}</td>
                          <td style={{ padding: "9px 8px", color: C.elevation, fontFamily: "'JetBrains Mono'", fontSize: 11 }}>{Math.round(yd?.elev || 0)} m</td>
                          <td style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono'" }}>{y.avgPace ? fmtPace(y.avgPace) + "/km" : "—"}</td>
                          <td style={{ padding: "9px 8px", color: C.pace, fontWeight: 600, fontFamily: "'JetBrains Mono'" }}>{y.bestPace ? fmtPace(y.bestPace) + "/km" : "—"}</td>
                          <td style={{ padding: "9px 8px", color: C.dim, fontFamily: "'JetBrains Mono'", fontSize: 11 }}>{(y.km / y.runs).toFixed(1)} km</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: `2px solid ${C.pace}`, background: "rgba(16,185,129,0.04)" }}>
                      <td style={{ padding: "9px 8px", fontWeight: 800, color: C.pace }}>TOTAL</td>
                      <td style={{ padding: "9px 8px", fontWeight: 700, fontFamily: "'JetBrains Mono'" }}>{allAnalytics.count}</td>
                      <td style={{ padding: "9px 8px", fontWeight: 700, color: C.distance, fontFamily: "'JetBrains Mono'" }}>{fmtKm(allAnalytics.totalDist)} km</td>
                      <td style={{ padding: "9px 8px", color: C.dim, fontFamily: "'JetBrains Mono'", fontSize: 11 }}>{fmtDuration(allAnalytics.totalTime)}</td>
                      <td style={{ padding: "9px 8px", color: C.elevation, fontFamily: "'JetBrains Mono'", fontSize: 11 }}>{Math.round(allAnalytics.totalElev).toLocaleString()} m</td>
                      <td style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono'" }}>{fmtPace(allAnalytics.avgPacePerKm)}/km</td>
                      <td style={{ padding: "9px 8px", color: C.pace, fontWeight: 700, fontFamily: "'JetBrains Mono'" }}>{fmtPace(allAnalytics.bestPace)}/km</td>
                      <td style={{ padding: "9px 8px", color: C.dim, fontFamily: "'JetBrains Mono'", fontSize: 11 }}>{(allAnalytics.totalDist / 1000 / allAnalytics.count).toFixed(1)} km</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ChartCard>

            {/* Heatmap */}
            <ChartCard title="Activity Heatmap" subtitle="Number of activities per month across all years" style={{ marginBottom: 16 }}>
              <Heatmap data={allAnalytics.heatmap} />
            </ChartCard>

            {/* Monthly trend */}
            <ChartCard title="Monthly Volume (Last 24 Months)" subtitle="Distance and activity count per month">
              {(() => {
                const data = Object.entries(allAnalytics.monthly)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .slice(-24)
                  .map(([key, d]) => ({ month: key.slice(2), km: Math.round(d.km * 10) / 10, runs: d.runs }));
                return (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data} barSize={14}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                      <XAxis dataKey="month" tick={{ fill: C.dim, fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: C.faint, fontSize: 9, fontFamily: "'JetBrains Mono'" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="km" name="Distance (km)" fill={C.distance} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </ChartCard>
          </div>
        )}

        {/* ═══ DEVICES ═══ */}
        {tab === "devices" && (
          <div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              {/* Device breakdown pie */}
              <ChartCard title="Activities by Device" subtitle="Which watch/tracker recorded each activity" style={{ flex: "1 1 280px" }}>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={devicePie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} strokeWidth={0} paddingAngle={3}>
                      {devicePie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: "rgba(7,8,12,0.96)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 12 }}>
                          <div style={{ fontWeight: 700, color: d.color }}>{d.name}</div>
                          <div style={{ color: C.dim }}>{d.value} activities · {fmtKm(d.dist)} km</div>
                        </div>
                      );
                    }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                  {devicePie.map((d, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)",
                      border: `1px solid ${C.border}`
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</span>
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 12 }}>
                        <span style={{ fontWeight: 700 }}>{d.value}</span>
                        <span style={{ color: C.dim, marginLeft: 6 }}>{fmtKm(d.dist)} km</span>
                      </div>
                    </div>
                  ))}
                </div>
              </ChartCard>

              {/* Device stats */}
              <ChartCard title="Multi-Device Ecosystem" subtitle="Your connected devices and their contributions" style={{ flex: "1 1 350px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {devicePie.map((d, i) => {
                    const pct = Math.round(d.value / allAnalytics.count * 100);
                    return (
                      <div key={i}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: d.color }}>{d.name}</span>
                          <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 12 }}>{pct}%</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,0.04)", overflow: "hidden" }}>
                          <div style={{
                            height: "100%", borderRadius: 4, background: `linear-gradient(90deg, ${d.color}, ${d.color}88)`,
                            width: `${pct}%`, transition: "width 0.6s ease"
                          }} />
                        </div>
                        <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
                          {d.value} activities · {fmtKm(d.dist)} km total distance
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  marginTop: 16, padding: "12px 14px", borderRadius: 10,
                  background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.12)",
                  fontSize: 12, color: C.dim, lineHeight: 1.5
                }}>
                  💡 <strong style={{ color: C.text }}>Multi-source sync:</strong> All devices push to Strava automatically.
                  Strava deduplicates overlapping activities, so wearing Garmin + WHOOP simultaneously won't create double entries.
                </div>
              </ChartCard>
            </div>
          </div>
        )}

        {/* ═══ ACTIVITY LOG ═══ */}
        {tab === "log" && (
          <div style={{
            background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden"
          }}>
            <div style={{
              padding: "14px 20px", borderBottom: `1px solid ${C.border}`,
              display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                  Recent Activities
                  {overrideCount > 0 && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 6,
                      background: "rgba(16,185,129,0.12)", color: C.pace
                    }}>✏️ {overrideCount} override{overrideCount > 1 ? "s" : ""}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: C.dim }}>
                  Last 30 {typeFilter !== "all" ? getActivityType(typeFilter).label + " " : ""}activities
                  {yearFilter !== "all" ? ` in ${yearFilter}` : ""} · Click ✏️ to edit
                </div>
              </div>
              {lastSync && <div style={{ fontSize: 10, color: C.faint, fontFamily: "'JetBrains Mono'" }}>Last sync: {fmtDate(lastSync)}</div>}
            </div>
            <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
              {recentActivities.map((a, i) => {
                const at = getActivityType(a.type);
                const isRun = a.type === "Run" || a.type === "VirtualRun";
                const pace = isRun && a.distance > 0 ? a.moving_time / (a.distance / 1000) : 0;
                const hasOverride = a._hasOverride;

                return (
                  <div key={a.id}>
                    <div style={{
                      padding: "11px 20px", borderBottom: a._note ? "none" : `1px solid rgba(255,255,255,0.03)`,
                      display: "flex", alignItems: "center", gap: 12,
                      background: hasOverride ? "rgba(16,185,129,0.02)" : (i % 2 ? "rgba(255,255,255,0.008)" : "transparent"),
                      borderLeft: hasOverride ? `2px solid ${C.pace}` : "2px solid transparent"
                    }}>
                      {/* Type bar */}
                      <div style={{ width: 3, height: 36, borderRadius: 3, background: at.color, flexShrink: 0 }} />

                      {/* Icon + Date */}
                      <div style={{ width: 60, flexShrink: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{fmtShortDate(a.start_date)}</div>
                        <div style={{ fontSize: 9, color: C.faint }}>{new Date(a.start_date).getFullYear()}</div>
                      </div>

                      {/* Type badge */}
                      <div style={{
                        padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                        background: `${at.color}12`, color: at.color, whiteSpace: "nowrap", flexShrink: 0
                      }}>{at.icon} {at.label}</div>

                      {/* Name */}
                      <div style={{ flex: 1, fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 60 }}>
                        {a.name}
                        {hasOverride && <span style={{ fontSize: 9, color: C.pace, marginLeft: 6 }}>✏️ edited</span>}
                      </div>

                      {/* Device */}
                      <DeviceBadge device={a.device_name} />

                      {/* Stats */}
                      <div style={{ display: "flex", gap: 14, alignItems: "center", fontFamily: "'JetBrains Mono'", flexShrink: 0 }}>
                        {a.distance > 0 && (
                          <div style={{ textAlign: "right", width: 55 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.distance }}>{fmtKm(a.distance)} km</div>
                          </div>
                        )}
                        {isRun && pace > 0 && (
                          <div style={{ textAlign: "right", width: 50 }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{fmtPace(pace)}/km</div>
                          </div>
                        )}
                        <div style={{ textAlign: "right", width: 45, color: C.dim, fontSize: 11 }}>
                          {fmtDuration(a.moving_time)}
                        </div>
                        {a.average_heartrate > 0 && (
                          <div style={{ textAlign: "right", width: 48, color: C.heart, fontSize: 11, fontWeight: 500 }}>
                            {Math.round(a.average_heartrate)} bpm
                          </div>
                        )}
                        {a.calories > 0 && (
                          <div style={{ textAlign: "right", width: 50, color: C.calories, fontSize: 11, fontWeight: 500 }}>
                            🔥 {Math.round(a.calories)}
                          </div>
                        )}
                        {a.suffer_score > 0 && (
                          <div title="Strava Relative Effort" style={{ textAlign: "right", width: 36, color: "#ef4444", fontSize: 10, fontWeight: 600, padding: "2px 5px", borderRadius: 4, background: "rgba(239,68,68,0.08)" }}>
                            {Math.round(a.suffer_score)}
                          </div>
                        )}
                      </div>

                      {/* Edit button */}
                      <button onClick={() => setEditingActivity(a)} style={{
                        background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`,
                        color: C.dim, width: 30, height: 30, borderRadius: 7,
                        cursor: "pointer", fontSize: 12, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.15s ease"
                      }}
                      onMouseEnter={e => { e.target.style.background = "rgba(16,185,129,0.12)"; e.target.style.color = C.pace; e.target.style.borderColor = C.pace; }}
                      onMouseLeave={e => { e.target.style.background = "rgba(255,255,255,0.04)"; e.target.style.color = C.dim; e.target.style.borderColor = C.border; }}
                      >✏️</button>
                    </div>
                    {/* Note row */}
                    {a._note && (
                      <div style={{
                        padding: "4px 20px 8px 86px", fontSize: 11, color: C.dim,
                        borderBottom: `1px solid rgba(255,255,255,0.03)`,
                        background: "rgba(16,185,129,0.02)", fontStyle: "italic"
                      }}>📝 {a._note}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ WHOOP TAB ═══ */}
        {tab === "training" && (
          <WorkoutPlan />
        )}

        {tab === "whoop" && whoopConnected && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeUp 0.3s ease-out" }}>
            {/* WHOOP Summary Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
              {[
                { label: "AVG RECOVERY", value: `${whoopSummary.avgRecovery}%`, color: whoopSummary.avgRecovery >= 67 ? "#44b700" : whoopSummary.avgRecovery >= 34 ? "#f59e0b" : "#ef4444", icon: "💚" },
                { label: "AVG HRV", value: `${whoopSummary.avgHrv} ms`, color: "#06b6d4", icon: "💓", sub: "RMSSD" },
                { label: "AVG RHR", value: `${whoopSummary.avgRhr} bpm`, color: "#ef4444", icon: "❤️" },
                { label: "AVG SLEEP", value: `${whoopSummary.avgSleepHrs} hrs`, color: "#8b5cf6", icon: "😴" },
                { label: "AVG STRAIN", value: `${whoopSummary.avgStrain}`, color: "#3b82f6", icon: "🔥", sub: "out of 21" },
                { label: "DATA RANGE", value: `${whoopSummary.days} days`, color: C.dim, icon: "📅" },
              ].map((card, i) => (
                <div key={i} style={{
                  background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`,
                  padding: "14px 16px", animation: `fadeUp 0.3s ease-out ${i * 0.05}s both`
                }}>
                  <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase", letterSpacing: 1, fontFamily: "'JetBrains Mono'", marginBottom: 6 }}>
                    {card.icon} {card.label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: card.color, fontFamily: "'JetBrains Mono'" }}>{card.value}</div>
                  {card.sub && <div style={{ fontSize: 10, color: C.faint, marginTop: 2 }}>{card.sub}</div>}
                </div>
              ))}
            </div>

            {/* Recovery Trend */}
            {whoopRecoveryChart.length > 0 && (
              <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: 20 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>💚 Recovery Score (Last 90 Days)</div>
                <div style={{ fontSize: 11, color: C.dim, marginBottom: 16 }}>Daily recovery % — Green (67–100), Yellow (34–66), Red (0–33)</div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={whoopRecoveryChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" tick={{ fill: C.faint, fontSize: 9 }} interval="preserveStartEnd" />
                    <YAxis domain={[0, 100]} tick={{ fill: C.faint, fontSize: 9 }} />
                    <Tooltip contentStyle={{ background: "#111", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="recovery" stroke="#44b700" fill="rgba(68,183,0,0.15)" strokeWidth={2} name="Recovery %" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* HRV + RHR Trend */}
            {whoopRecoveryChart.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: 20 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>💓 HRV Trend (RMSSD)</div>
                  <div style={{ fontSize: 11, color: C.dim, marginBottom: 16 }}>Heart Rate Variability in milliseconds — higher is better</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={whoopRecoveryChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="date" tick={{ fill: C.faint, fontSize: 9 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: C.faint, fontSize: 9 }} />
                      <Tooltip contentStyle={{ background: "#111", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                      <Line type="monotone" dataKey="hrv" stroke="#06b6d4" strokeWidth={2} dot={false} name="HRV (ms)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: 20 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>❤️ Resting Heart Rate</div>
                  <div style={{ fontSize: 11, color: C.dim, marginBottom: 16 }}>RHR in bpm — lower is generally better for athletes</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={whoopRecoveryChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="date" tick={{ fill: C.faint, fontSize: 9 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: C.faint, fontSize: 9 }} />
                      <Tooltip contentStyle={{ background: "#111", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                      <Line type="monotone" dataKey="rhr" stroke="#ef4444" strokeWidth={2} dot={false} name="RHR (bpm)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Sleep Architecture */}
            {whoopSleepChart.length > 0 && (
              <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: 20 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>😴 Sleep Architecture</div>
                <div style={{ fontSize: 11, color: C.dim, marginBottom: 16 }}>Nightly sleep stages breakdown (hours) — SWS (Slow Wave Sleep) = Deep Sleep</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={whoopSleepChart} stackOffset="none">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" tick={{ fill: C.faint, fontSize: 9 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: C.faint, fontSize: 9 }} label={{ value: "Hours", angle: -90, position: "insideLeft", fill: C.faint, fontSize: 9 }} />
                    <Tooltip contentStyle={{ background: "#111", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="deep" stackId="sleep" fill="#3b82f6" name="Deep (SWS)" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="rem" stackId="sleep" fill="#8b5cf6" name="REM" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="light" stackId="sleep" fill="#06b6d4" name="Light" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Daily Strain */}
            {whoopStrainChart.length > 0 && (
              <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: 20 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>🔥 Daily Strain</div>
                <div style={{ fontSize: 11, color: C.dim, marginBottom: 16 }}>WHOOP Day Strain (0–21 scale) — measures total cardiovascular load across all activities</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={whoopStrainChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" tick={{ fill: C.faint, fontSize: 9 }} interval="preserveStartEnd" />
                    <YAxis domain={[0, 21]} tick={{ fill: C.faint, fontSize: 9 }} />
                    <Tooltip contentStyle={{ background: "#111", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="strain" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Strain" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* WHOOP Workout Log */}
            {whoopWorkoutList.length > 0 && (
              <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>🏋️ WHOOP Workouts</div>
                  <div style={{ fontSize: 11, color: C.dim }}>Recent workouts with strain, calories, and HR zones</div>
                </div>
                <div style={{ maxHeight: "40vh", overflowY: "auto" }}>
                  {whoopWorkoutList.map((w, i) => (
                    <div key={w.id} style={{
                      padding: "10px 20px", borderBottom: `1px solid rgba(255,255,255,0.03)`,
                      display: "flex", alignItems: "center", gap: 12,
                      background: i % 2 ? "rgba(255,255,255,0.008)" : "transparent"
                    }}>
                      <div style={{ width: 60, fontSize: 11, fontWeight: 600, color: C.dim }}>{w.date}</div>
                      <div style={{
                        padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                        background: "rgba(68,183,0,0.1)", color: "#44b700", whiteSpace: "nowrap", minWidth: 70
                      }}>🏋️ {w.sport}</div>
                      <div style={{ flex: 1 }} />
                      <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 13, fontWeight: 700, color: "#3b82f6" }}>
                        {w.strain} <span style={{ fontSize: 9, color: C.dim, fontWeight: 400 }}>strain</span>
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 11, color: C.calories, width: 55, textAlign: "right" }}>
                        🔥 {w.calories}
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 11, color: C.heart, width: 48, textAlign: "right" }}>
                        {w.avgHr} bpm
                      </div>
                      {w.distance && (
                        <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 11, color: C.distance, width: 55, textAlign: "right" }}>
                          {w.distance} km
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {whoopData.recovery.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: C.dim }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>💚</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>WHOOP connected — syncing data...</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>Click "Sync Now" to fetch your recovery, sleep, and strain data</div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{
          textAlign: "center", padding: "30px 0 20px", fontSize: 10, color: C.faint,
          fontFamily: "'JetBrains Mono'"
        }}>
          🏃 Miha's Fitness Command Center · Powered by Strava + WHOOP APIs
          <br />
          Garmin · WHOOP · Apple Watch → Strava → Dashboard
          {lastSync && <><br />Last synced: {new Date(lastSync).toLocaleString()}</>}
          {overrideCount > 0 && <><br />✏️ {overrideCount} local override{overrideCount > 1 ? "s" : ""} applied</>}
        </div>
      </main>

      {/* Edit Modal */}
      {editingActivity && (
        <EditModal
          activity={editingActivity}
          onSave={saveOverride}
          onClear={clearOverride}
          onClose={() => setEditingActivity(null)}
        />
      )}
    </div>
  );
}
